/**
 * Knob - SVG-based rotary knob component
 *
 * Features:
 * - Vertical drag to adjust (up = increase, down = decrease)
 * - Shift+drag for fine control (10x precision)
 * - Double-click to reset to default
 * - Visual feedback during interaction
 */

export class Knob {
  constructor(options) {
    this.name = options.name || 'param';
    this.label = options.label || this.name;
    this.min = options.min ?? 0;
    this.max = options.max ?? 1;
    this.default = options.default ?? (this.min + this.max) / 2;
    this.value = options.value ?? this.default;
    this.step = options.step ?? (this.max - this.min) / 100;
    this.unit = options.unit || '';
    this.decimals = options.decimals ?? 2;
    this.onChange = options.onChange || (() => {});

    // Visual settings
    this.size = options.size || 36;
    this.accentColor = options.accentColor || '#00ff88';

    // State
    this.isDragging = false;
    this.dragStartY = 0;
    this.dragStartValue = 0;

    // Create DOM
    this.element = this.createElement();
    this.bindEvents();
  }

  createElement() {
    const container = document.createElement('div');
    container.className = 'knob-container';
    container.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      width: ${this.size + 8}px;
      user-select: none;
      cursor: ns-resize;
    `;

    // Value display - scale with knob size
    const valueFontSize = this.size < 26 ? 8 : this.size < 30 ? 9 : this.size < 36 ? 10 : 11;
    this.valueDisplay = document.createElement('div');
    this.valueDisplay.className = 'knob-value';
    this.valueDisplay.style.cssText = `
      font-size: ${valueFontSize}px;
      color: #fff;
      height: ${valueFontSize + 3}px;
      margin-bottom: 1px;
      font-family: monospace;
    `;
    this.updateValueDisplay();

    // SVG knob
    const svgNS = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(svgNS, 'svg');
    svg.setAttribute('width', this.size);
    svg.setAttribute('height', this.size);
    svg.setAttribute('viewBox', '0 0 36 36');

    const cx = 18, cy = 18, r = 15;

    // Outer ring
    const outerRing = document.createElementNS(svgNS, 'circle');
    outerRing.setAttribute('cx', cx);
    outerRing.setAttribute('cy', cy);
    outerRing.setAttribute('r', r + 2);
    outerRing.setAttribute('fill', 'none');
    outerRing.setAttribute('stroke', '#333');
    outerRing.setAttribute('stroke-width', '2');

    // Knob face
    const knobFace = document.createElementNS(svgNS, 'circle');
    knobFace.setAttribute('cx', cx);
    knobFace.setAttribute('cy', cy);
    knobFace.setAttribute('r', r);
    knobFace.setAttribute('fill', '#252525');

    // Indicator line
    this.indicator = document.createElementNS(svgNS, 'line');
    this.indicator.setAttribute('x1', cx);
    this.indicator.setAttribute('y1', cy);
    this.indicator.setAttribute('stroke', '#888');
    this.indicator.setAttribute('stroke-width', '2');
    this.indicator.setAttribute('stroke-linecap', 'round');
    this.updateIndicator();

    // Active glow (hidden by default)
    this.glowRing = document.createElementNS(svgNS, 'circle');
    this.glowRing.setAttribute('cx', cx);
    this.glowRing.setAttribute('cy', cy);
    this.glowRing.setAttribute('r', r + 2);
    this.glowRing.setAttribute('fill', 'none');
    this.glowRing.setAttribute('stroke', this.accentColor);
    this.glowRing.setAttribute('stroke-width', '2');
    this.glowRing.setAttribute('opacity', '0');

    svg.appendChild(outerRing);
    svg.appendChild(knobFace);
    svg.appendChild(this.indicator);
    svg.appendChild(this.glowRing);

    this.svg = svg;

    // Label - scale with knob size
    const labelFontSize = this.size < 26 ? 7 : this.size < 30 ? 8 : this.size < 36 ? 9 : 10;
    const label = document.createElement('div');
    label.className = 'knob-label';
    label.textContent = this.label;
    label.style.cssText = `
      font-size: ${labelFontSize}px;
      color: #888;
      text-transform: uppercase;
      margin-top: ${this.size < 30 ? 1 : 2}px;
      text-align: center;
      max-width: ${this.size + 10}px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    `;

    container.appendChild(this.valueDisplay);
    container.appendChild(svg);
    container.appendChild(label);

    return container;
  }

  updateIndicator() {
    // Map value to angle: 225° (min) to -45° (max), clockwise
    const normalized = (this.value - this.min) / (this.max - this.min);
    const startAngle = 225; // Bottom-left
    const endAngle = -45;   // Bottom-right
    const angle = startAngle - normalized * (startAngle - endAngle);
    const radians = (angle * Math.PI) / 180;

    const cx = 18, cy = 18, r = 12;
    const x2 = cx + Math.cos(radians) * r;
    const y2 = cy - Math.sin(radians) * r;

    this.indicator.setAttribute('x2', x2);
    this.indicator.setAttribute('y2', y2);
  }

  updateValueDisplay() {
    let displayValue = this.value.toFixed(this.decimals);
    // Remove trailing zeros after decimal
    if (this.decimals > 0) {
      displayValue = parseFloat(displayValue).toString();
    }
    this.valueDisplay.textContent = displayValue + this.unit;
  }

  setValue(value, triggerCallback = true) {
    const oldValue = this.value;
    this.value = Math.max(this.min, Math.min(this.max, value));

    this.updateIndicator();
    this.updateValueDisplay();

    if (triggerCallback && this.value !== oldValue) {
      this.onChange(this.value, this.name);
    }
  }

  reset() {
    this.setValue(this.default);
  }

  setActive(active) {
    this.glowRing.setAttribute('opacity', active ? '1' : '0');
    this.indicator.setAttribute('stroke', active ? this.accentColor : '#888');
  }

  bindEvents() {
    const startDrag = (e) => {
      e.preventDefault();
      this.isDragging = true;
      this.dragStartY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      this.dragStartValue = this.value;
      this.setActive(true);

      document.addEventListener('mousemove', onDrag);
      document.addEventListener('mouseup', endDrag);
      document.addEventListener('touchmove', onDrag);
      document.addEventListener('touchend', endDrag);
    };

    const onDrag = (e) => {
      if (!this.isDragging) return;

      const clientY = e.clientY ?? e.touches?.[0]?.clientY ?? 0;
      const deltaY = this.dragStartY - clientY;

      // Shift for fine control
      const sensitivity = e.shiftKey ? 0.1 : 1;
      const range = this.max - this.min;
      const pixelsPerFullRange = 150;

      const deltaValue = (deltaY / pixelsPerFullRange) * range * sensitivity;
      this.setValue(this.dragStartValue + deltaValue);
    };

    const endDrag = () => {
      this.isDragging = false;
      this.setActive(false);

      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', endDrag);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', endDrag);
    };

    this.element.addEventListener('mousedown', startDrag);
    this.element.addEventListener('touchstart', startDrag);

    // Double-click to reset
    this.element.addEventListener('dblclick', (e) => {
      e.preventDefault();
      this.reset();
    });
  }

  getElement() {
    return this.element;
  }

  getValue() {
    return this.value;
  }

  destroy() {
    this.element.remove();
  }
}
